"use client";
import classNames from "classnames";
import { useCustomWallet } from "components/providers";
import { BasicProps } from "components/types/BasicProps";
import { FC, useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore
import Blockies from "react-blockies";
import { accountLiterals } from "./literals";
import { TbCopy } from "react-icons/tb";
import { TbQrcode } from "react-icons/tb";
import Link from "next/link";
import { useUAccountEtherscanHref } from "./hooks";
import { useCopyToClipboard } from "react-use";
import currenciesModule from "logic/CurrenciesModule";
import { displayDollarValue } from "utils";
import TokenIcon from "components/icons/TokenIcon";
import { useModalQr } from "./AccountProvider";
import { EtherscanIcon } from "components/icons";
import { Popover } from "components/Popover";

type Props = BasicProps & {
  totalAssets?: number;
  ethBalance?: bigint;
};

const {
  generalData: {
    accountLabel,
    ethValueLabel,
    usdValueLabel,
    totalAssetsLabel,
    copiedFeedback,
    copyDescription,
  },
} = accountLiterals;

const nativeCurrency = currenciesModule.nativeCurrency;

export const UAccountGeneralData: FC<Props> = (props) => {
  const { className, totalAssets, ethBalance } = props;
  const { smartAddress } = useCustomWallet();
  const ref = useRef<HTMLCanvasElement>(null);
  const [, copyToClipboard] = useCopyToClipboard();
  const [usdBalance, setUsdBalance] = useState<string>();
  const [, toggleQrModal] = useModalQr();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [popoverLiteral, setPopoverLiteral] = useState<string>(copyDescription);

  const etherscanHref = useUAccountEtherscanHref();

  const balanceToShow = useMemo<string>(
    () =>
      ethBalance !== undefined
        ? currenciesModule.nativeCurrency.formatAmount(ethBalance)
        : "-",
    [ethBalance]
  );

  useEffect(() => {
    if (isPopoverOpen) {
      const id = setTimeout(() => {
        setIsPopoverOpen(false);
        setPopoverLiteral(copyDescription);
      }, 2000);

      return () => clearTimeout(id);
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    if (ref.current) {
      // @ts-ignore
      ref.current.identicon.style.width = "109px";
      // @ts-ignore
      ref.current.identicon.style.height = "109px";
    }
  }, [ref]);

  useEffect(() => {
    if (ethBalance !== undefined) {
      handleCalculateUsdBalance(ethBalance);
    }
  }, [ethBalance]);

  const handleCalculateUsdBalance = async (ethBalance: bigint) => {
    const usdBalance = await nativeCurrency.parseToDollar(ethBalance);

    setUsdBalance(displayDollarValue(usdBalance));
  };

  const handleCopyClick = () => {
    copyToClipboard(smartAddress || "");

    setPopoverLiteral(copiedFeedback);
    setIsPopoverOpen(true);
  };

  return (
    <>
      <section
        className={classNames("p-0.5 border-unlockd rounded-[20px]", className)}
      >
        <div className="w-full rounded-[20px] bg-secondary py-[49px] px-[58px] flex gap-[30px] items-center">
          {smartAddress && (
            <Blockies
              className="rounded-full"
              ref={ref}
              scale={8}
              seed={smartAddress}
            />
          )}
          <div className="w-full flex justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-2xlfont-bold">{accountLabel}</span>
              <div className="flex gap-2 items-center">
                <span className="w-[266px] text-2xl font-medium text-ellipsis overflow-hidden whitespace-nowrap">
                  {smartAddress}
                </span>
                <div className="group relative w-[30px] h-full">
                  <TbCopy
                    className="w-[30px] h-full text-uyellow rotate-90 cursor-pointer"
                    onClick={handleCopyClick}
                  />
                  {isPopoverOpen && (
                    <Popover className="absolute -bottom-[58px] left-[15px] -translate-x-1/2 w-[200px] h-[52px]">
                      {popoverLiteral}
                    </Popover>
                  )}
                  <Popover className="hidden group-hover:flex absolute -bottom-[58px] left-[15px] -translate-x-1/2 w-[200px] h-[52px]">
                    {popoverLiteral}
                  </Popover>
                </div>
                <TbQrcode
                  className="w-[30px] h-full text-uyellow cursor-pointer"
                  onClick={toggleQrModal}
                />
                <Link href={etherscanHref} target="_blank">
                  <EtherscanIcon
                    className="w-[25px] h-[25px]"
                    color="uyellow"
                  />
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold">{totalAssetsLabel}</span>
              <span className="text-2xl font-medium">
                {totalAssets !== undefined ? totalAssets : "-"}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold">{ethValueLabel}</span>
              <span className="text-2xl font-medium flex items-center gap-2.5">
                {balanceToShow}
                <TokenIcon className="w-6 h-6" currency={nativeCurrency} />
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold">{usdValueLabel}</span>
              <span className="text-2xl font-medium">{usdBalance}</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
